import React, {useMemo} from 'react';
import styled from 'styled-components';
import {FlexColumn, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from './ReportBuilderContext';
import {ReportToolbar} from './ReportToolbar';
import {EmptyReportState} from './EmptyReportState';
import InlineLoader from '../../forecast-app/shared/components/inline-loader/inline_loader';
import {ReportErrorState} from './ReportErrorState';
import {LeaveBuilderPrompt} from './LeaveBuilderPrompt';
import {ReportTable} from './ReportTable/ReportTable';
import {TestHook} from './TestHook';
import {
	budgetTypePathFor,
	GroupTotal,
	groupingCellPaths,
	unproducibleGroupingTokens,
	unwrapDeliverableRows,
	useClientGrouping,
	useTableTotals,
} from './ReportTable/useClientGrouping';
import {AuxiliaryDetail} from './AuxiliaryDetail';
import {groupingTokenName} from './GroupByControl';
import {grainRootOf} from './data/definitionFields';
import {rowCountMessagesOf, rowGrainOf} from './reportGrain';
import {createCellFormatter} from './ReportTable/cellDisplay';
import {AIBanner} from './TopBar/AIBanner';
import {EditTopBar} from './TopBar/EditTopBar';
import {LockStateBanner} from './TopBar/LockStateBanner';
import {PublishStateBanner} from './TopBar/PublishStateBanner';
import {ReportPageHeader} from './TopBar/ReportPageHeader';
import {ViewTopBar} from './TopBar/ViewTopBar';

const tokens = tokensForThemeProvider.sky.sys;

/** Horizontal page gutter (32px in the design's 1920px frame) and vertical rhythm so the page header,
 * controls row, AI banner, and table are all inset from the viewport edges and evenly spaced,
 * rather than full-bleed — see the design. the design's `Page Header`
 * container has asymmetric vertical padding
 * (`paddingTop=8 paddingBottom=16`, not a uniform 16) and `itemSpacing=16` between its children
 * — not the 12px gap previously used here. */
/* DS FlexColumn defaults to flex:1, which would stretch this to fill the full page height (its
 * parent `Surface` fills the viewport); any leftover height would then be redistributed as growth
 * among children that are themselves FlexColumn/FlexRow-based (the header, the top bar), pushing
 * them apart by an amount that shrinks as the table grows with more rows. Pin to content size so
 * the page's vertical rhythm stays fixed regardless of row count. */
/* The loader paints an 80px spinner as a centred background image and sizes itself to its parent, so
 * it needs a slot with real height: this column is auto-height, where `height: 100%` resolves to the
 * loader's own content height, which is zero. Roughly a few table rows' worth, so the surface does not
 * visibly jump when the rows land. */
const LoaderSlot = styled.div`
	min-height: 240px;
	display: flex;
`;

/** The row-count line and whatever fills the table's place, as one block.
 *
 * `SurfaceWrapper`'s 16px rhythm separates the page's independent parts; the caption is not one of
 * those - it describes the table directly beneath it, and the design has it 4px clear of it
 * (caption bottom 178, table top 182 in the 1920px frame) rather than a full step away. A plain
 * flex div, not the DS `FlexColumn`, for the same reason `LoaderSlot` is one: `FlexColumn` defaults
 * to `flex: 1` and would stretch this block to absorb the page's leftover height. Safe to wrap
 * `ReportTable` this way because its own root is `width: 100%` with no flex growth, so it does not
 * depend on being a direct child of the surface column. */
const TableBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.spacing['100']};
`;

const SurfaceWrapper = styled(FlexColumn)`
	gap: ${tokens.spacing['400']};
	width: 100%;
	padding: ${tokens.spacing['200']} ${tokens.spacing['800']} ${tokens.spacing['400']};
	flex: 0 0 auto;
`;

/** Assembled AI Report Builder surface body: `LockStateBanner` first and full-bleed, outside the
 * padded wrapper so an expired editing session is announced across the whole content area under the
 * global navigation before anything else on the page; then, inside the page gutter, the page header
 * (Reports eyebrow + report name
 * heading/pencil), then the mode/role-dependent top bar, then `PublishStateBanner` — telling the
 * editor whether readers are actually isolated from in-progress edits, and rendering nothing at all
 * for a reader, whose last-published date is a caption on the page header instead — then AIBanner —
 * shown only when the report's
 * origin is AI-generated AND the surface is in edit mode, since showing it for a report opened from
 * anywhere else would claim an origin that isn't true, and a reader has no conditions to reconcile
 * against the prompt it quotes (per the design, the banner otherwise sits BELOW the name/period/Save-Report
 * row, not above it) — then
 * the merged conditions/Group By/Columns `ReportToolbar` strip (edit mode only — view mode goes
 * straight from the top bar to the table, as the design renders it), and finally the `TableBlock`:
 * the row-count caption — what the report lists, how much of it is on screen, and that the figures
 * only cover what this reader may see — above the grouped `ReportTable` — or, when the fetch
 * matched zero rows, `EmptyReportState` in its place, stating that nothing matched and how to widen
 * the report instead of a bare empty grid, or `ReportErrorState` when the fetch failed outright
 * (which is not the same thing as matching nothing); the caption is suppressed for all three of
 * those substitutes, each of which already speaks for itself. This
 * is the surface BODY only — no modal chrome. Row data and its grouping/table state
 * all come from `useReportBuilder()`; grouping is applied client-side via `useClientGrouping`
 * before handing rows to `ReportTable`. Mode switching is done solely via the "Save Report"
 * (edit→view) and "Edit Report" (view→edit) buttons in the top bars — no mode-toggle UI here. */
export function ReportBuilderSurface(): JSX.Element {
	const {
		definition,
		mode,
		source,
		tableState,
		updateTableState,
		toggleFavorite,
		rows,
		totalCount,
		cappedTotalEstimate,
		totalIsFloor,
		capped,
		grouping,
		hasFetched,
		fetchFailed,
		fetchAccessDenied,
		retryFetch,
		taxonomy,
	} = useReportBuilder();
	const intl = useIntl();
	const {formatMessage} = intl;

	// Grouping reads a cell per row, and which cell a token means is per grain: `client` is
	// `task.project.client.name` on a task report and `projectStatus.client.name` on a project one.
	//
	// `budgetTypePath` is resolved the same way and for the same reason. A GROUP's duration total is
	// only honest if the rows it summed share a budget type, and which cell holds that is a per-grain
	// fact - so it is answered here, from what the catalog actually publishes, rather than inside
	// the grain-agnostic grouping module. A grain that publishes no such field gets no rule, which
	// is what the task grain wants. The whole-table total never takes it: see `computeTableTotals`.
	const groupingOptions = useMemo(() => {
		const root = grainRootOf(definition);
		return {
			cellPaths: groupingCellPaths(root, taxonomy),
			taxonomy,
			budgetTypePath: budgetTypePathFor(root, taxonomy),
			formatLabel: (messageId: string) => formatMessage({id: messageId}),
			// Group headers read like the cells beneath them (identity on every grain but the audit log).
			formatCell: createCellFormatter(taxonomy, rowGrainOf(definition), {intl}),
		};
	}, [definition, taxonomy, formatMessage, intl]);
	// The task grain's fetch can nest tasks under a promoted deliverable row, which is not something
	// the reader asked for: a report shows its tasks plain, and one grouped by deliverable builds its
	// deliverable groups from each task's own deliverable cell. So the tier is flattened here, for the
	// ungrouped table as well as the grouped one, and a deliverable row never reaches the table.
	const plainRows = useMemo(() => unwrapDeliverableRows(rows), [rows]);
	const groupedRows = useClientGrouping(plainRows, grouping, groupingOptions);
	// Computed from the UNGROUPED rows: the totals are over every row either way (grouping only
	// buckets them), and reading the flat list keeps the footer independent of whether grouping is on.
	const rawTableTotals = useTableTotals(plainRows);
	// A capped fetch only ever summed the rows it actually received, so a whole-table total over a
	// truncated result is not an honest number for ANY duration column - including one `rawTableTotals`
	// happened to compute cleanly, since "clean over 1000 of 4000 rows" is exactly the
	// wrong-number-stated-confidently failure this feature exists to remove. Overriding every present
	// entry (rather than leaving an already-withheld column's own reason) also keeps the footer's
	// explanation honest: the reader's real problem is the truncation, not whatever the visible rows
	// happen to disagree on. Computed here rather than inside `computeTableTotals` because "the fetch
	// was capped" is a fact about THIS surface's data source, not something the grouping module - which
	// only ever sees the rows it is handed - has any way to know. Group totals are untouched: a group
	// header is scoped by its own label, not the whole report, so it keeps reporting on the rows it
	// actually has.
	const tableTotals = useMemo<Record<string, GroupTotal>>(() => {
		if (!capped) {
			return rawTableTotals;
		}
		const withheld: Record<string, GroupTotal> = {};
		for (const path of Object.keys(rawTableTotals)) {
			withheld[path] = {kind: 'withheld', reason: 'truncatedRows'};
		}
		return withheld;
	}, [rawTableTotals, capped]);

	const totalLabel = totalIsFloor ? `${totalCount}+` : String(totalCount);
	// What the line says, and which of the grain's two wordings says it.
	//
	// Shown whenever there are rows on screen, not only when the fetch was truncated: the figures
	// only ever cover the projects this reader can see, and the reader who most needs telling that -
	// one who can see 40 of 300 - is precisely the one whose result was never capped. The truncated
	// wording is the only one that mentions Export, because on an uncapped result the full set is
	// already on screen.
	//
	// The entity it names ("tasks", "projects") comes from the grain the ROWS are keyed under, so a
	// report cannot describe itself as something it is not - and a grain added later cannot compile
	// without saying what its rows are called. See `rowCountMessagesOf`.
	//
	// `rowGrainOf`, not `grainOf`: the caption has to read the same signal as the rows it describes.
	// `grainOf` gives the stored `reportType` precedence, and a report saved before the grain was
	// recorded carries the task type over project-status fields - the mapper keys those rows as
	// projects while the type says tasks, so a type-based caption would head a table of projects
	// with "tasks". Same field root the grouping options above are resolved from.
	const rowGrain = rowGrainOf(definition);
	// The audit log is the one grain the app authors itself, from a Create-a-New-Report card. Two
	// things follow from that and both are read off the grain rather than off what storage recorded:
	// there was no prompt to have generated it, and the report has to say what it covers.
	const isAuditLog = rowGrain === 'TIME_REG_AUDIT_LOG';
	const rowCountMessages = rowCountMessagesOf(rowGrain);
	const rowCountText = capped
		? formatMessage({id: rowCountMessages.capped}, {shown: cappedTotalEstimate, total: totalLabel})
		: formatMessage({id: rowCountMessages.uncapped}, {count: totalCount});
	// Suppressed unless rows actually reached the screen. The loading, failed and zero-match branches
	// below each replace the table with something that already speaks for itself, and `Showing 0
	// matching tasks you have access to.` above `No rows match these filters` is a worse-worded
	// second copy of it.
	const rowCountLine =
		hasFetched && !fetchFailed && rows.length > 0 ? (
			<AuxiliaryDetail data-cy={'ai-report-builder-row-count'}>{rowCountText}</AuxiliaryDetail>
		) : null;

	// A grouping level the report cannot produce is dropped by `groupRows` rather than rendered as one
	// "No <field>" group over every row. Dropping it silently would leave the reader looking
	// at a report that ignores part of what it was asked to do, with nothing to say so - so name it.
	// Shown under the same condition as the row count - only when rows are on screen to be grouped -
	// and only once the catalog is known. Rows can arrive before it, and a project-status token cannot
	// be resolved without it; that is "not known yet", not "cannot be grouped", and saying the latter
	// would be false for as long as the catalog is loading, or for good if it never loads.
	const unproducibleGrouping = taxonomy ? unproducibleGroupingTokens(grouping, groupingOptions.cellPaths, taxonomy) : [];
	// Named where the reader would recognise the name; a level with no such name is counted instead,
	// never printed in its wire spelling.
	const unproducibleNames = unproducibleGrouping
		.map(token => groupingTokenName(token, taxonomy, groupingOptions.cellPaths, formatMessage))
		.filter((name): name is string => name !== null);
	const unnamedUnproducible = unproducibleGrouping.length - unproducibleNames.length;
	const groupingUnavailableLine =
		rowCountLine && unproducibleGrouping.length > 0 ? (
			<>
				{unproducibleNames.length > 0 ? (
					<AuxiliaryDetail data-cy={'ai-report-builder-grouping-unavailable'}>
						{formatMessage(
							{id: 'ai_report_builder.grouping_unavailable'},
							{fields: unproducibleNames.join(', '), count: unproducibleNames.length}
						)}
					</AuxiliaryDetail>
				) : null}
				{unnamedUnproducible > 0 ? (
					<AuxiliaryDetail data-cy={'ai-report-builder-grouping-unavailable'}>
						{formatMessage({id: 'ai_report_builder.grouping_unavailable_unnamed'}, {count: unnamedUnproducible})}
					</AuxiliaryDetail>
				) : null}
			</>
		) : null;

	return (
		<TestHook data-cy={'ai-report-builder-surface'}>
			{/* Outside `SurfaceWrapper` on purpose: the expired-session banner is full-bleed, spanning the
			 * content area directly under the global navigation and above the page header, rather than
			 * sitting inside the page's horizontal gutter like the other banners. */}
			<LockStateBanner />

			<SurfaceWrapper>
				<LeaveBuilderPrompt />

				<ReportPageHeader />

				{mode === 'edit' ? <EditTopBar /> : <ViewTopBar />}

				<PublishStateBanner />

				{/* Not `source` alone: a report saved before authorship was derived carries source=AI in
				 * storage, and the banner would go on claiming an audit log came from a prompt nobody
				 * typed. The grain is the fact; the stored field is a record of one. */}
				{source === 'AI' && mode === 'edit' && !isAuditLog ? <AIBanner /> : null}

				{mode === 'edit' ? <ReportToolbar /> : null}

				<TableBlock>
					{/* Said in the report, not only in the documentation: an audit log that quietly omits a
					 * kind of time reads as a complete record of a smaller truth. Shown whether or not any
					 * rows arrived — an empty audit log is exactly when a reader needs to know what was in
					 * scope to be empty of. */}
					{isAuditLog ? (
						<AuxiliaryDetail data-cy={'ai-report-builder-audit-coverage'}>
							{formatMessage({id: 'report.audit_log.coverage_note'})}
						</AuxiliaryDetail>
					) : null}

					{rowCountLine}

					{groupingUnavailableLine}

					{fetchFailed ? (
						<ReportErrorState onRetry={retryFetch} accessDenied={fetchAccessDenied} />
					) : !hasFetched ? (
						/* Only a fetch with nothing to show yet gets a placeholder. Once rows have arrived a
						   re-fetch keeps them on screen: flashing a loader over data that is still correct reads
						   as the page resetting rather than as progress, and the rows it replaces are usually the
						   ones being refined.

						   Keyed off `hasFetched` rather than a separate in-flight flag because the fetch starts in
						   an effect, which runs AFTER the first paint — an in-flight flag is still false at that
						   point, so the surface would paint an empty table for one frame before the loader
						   appeared. A failed fetch clears `hasFetched`, so this also covers the retry after an
						   error, where there are again no rows to preserve.

						   `InlineLoader` rather than `UploadingOverlay`: this slot sits inside the page, and the
						   overlay is `position: fixed` over the whole viewport below the top nav, so it would
						   escape this row and cover the toolbar and banner above it — leaving them visible but
						   swallowing every click. Both draw the same spinner. */
						<LoaderSlot data-cy={'ai-report-builder-rows-loading'}>
							<InlineLoader />
						</LoaderSlot>
					) : rows.length === 0 ? (
						<EmptyReportState />
					) : (
						<ReportTable
							rows={groupedRows}
							tableTotals={tableTotals}
							definition={definition}
							taxonomy={taxonomy}
							tableState={tableState}
							onTableStateChange={updateTableState}
							onToggleFavorite={toggleFavorite}
						/>
					)}
				</TableBlock>
			</SurfaceWrapper>
		</TestHook>
	);
}
